"use client";

import { ChevronDown } from "lucide-react";
import { usePathname } from "next/navigation";
import { useState, type FocusEvent } from "react";
import { PayloadLink } from "@/components/shared/PayloadLink";
import { useMegaMenu } from "./MegaMenuContext";
import { useNavCollapsed } from "./ScrollableHeader";
import { NavIcon } from "./NavIcon";
import { cn } from "@/lib/utils";
import { isNavItemActive, type NavItemConfig } from "@/lib/navigation-utils";
import type { Media } from "@/payload-types";

// Re-export for backwards compatibility
export type { NavItemConfig } from "@/lib/navigation-utils";

interface NavItemProps {
  item: NavItemConfig;
}

type UIAssetLike =
  | {
      assetType?: "static" | "lottie" | null;
      posterImage?: Media | string | number | null;
      lottieFile?: Media | string | number | null;
    }
  | string
  | null
  | undefined;

export function NavItem({ item }: NavItemProps) {
  const [isIconActive, setIsIconActive] = useState(false);
  const { activeDropdown, setActiveDropdown } = useMegaMenu();
  const collapsed = useNavCollapsed();
  const pathname = usePathname() ?? "";
  const isActive = isNavItemActive(pathname, item);
  const isServicesLayout = item.megaMenuType === "services-layout";
  const hasServicesLayout = isServicesLayout && item.servicesLayout?.columns && item.servicesLayout.columns.length > 0;
  const hasDropdown = item.type === "dropdown" && (
    (item.children && item.children.length > 0) ||
    hasServicesLayout
  );

  // Desktop version - uses MegaMenu context for dropdowns
  const isOpenDesktop = activeDropdown === item.id;

  // For dropdowns, use dropdownPage for the main link
  const dropdownPage = item.dropdownPage ?? null;

  const itemWithIconAsset = item as NavItemConfig & {
    icon?: Media | string | number | null;
    iconAsset?: UIAssetLike;
  };
  const icon = itemWithIconAsset.icon;
  const iconAsset = itemWithIconAsset.iconAsset;

  const handleMouseEnter = () => {
    setIsIconActive(true);
    if (hasDropdown) {
      setActiveDropdown(item.id || null, item);
    }
  };

  const handleMouseLeave = () => {
    setIsIconActive(false);
    if (hasDropdown) {
      setActiveDropdown(null);
    }
  };

  const handleFocusCapture = () => {
    setIsIconActive(true);
  };

  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsIconActive(false);
    }
  };

  // Render the nav item content (icon + label + description)
  const navContent = (
    <div className={cn("flex items-center transition-all duration-300", collapsed ? "gap-1" : "gap-3")}>
      <NavIcon
        label={item.label}
        iconAsset={iconAsset}
        legacyIcon={icon}
        active={isIconActive}
        collapsed={collapsed}
      />
      <div className="flex flex-col">
        <span
          className={cn(
            "font-semibold text-lg 3xl:text-[20px] leading-tight transition-colors group-hover:text-brand",
            isActive ? "text-brand" : "text-white",
          )}
        >
          {item.label}
        </span>
        {item.description && (
          <span className={cn("text-white/60 text-[10px] leading-tight overflow-hidden transition-all duration-300", collapsed ? "opacity-0 max-h-0 max-w-0 mt-0" : "opacity-100 max-h-6 max-w-[16ch] 3xl:max-w-[18ch] mt-0.5")}>
            {item.description}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      {hasDropdown ? (
        <div className={cn("flex items-center px-1 cursor-pointer transition-all duration-300", collapsed ? "py-2" : "py-4")}>
          {dropdownPage ? (
            <PayloadLink
              type="page"
              page={dropdownPage}
              onClick={() => setActiveDropdown(null)}
              className="transition-colors"
            >
              {navContent}
            </PayloadLink>
          ) : (
            <div className="flex items-center gap-1">
              {navContent}
              <ChevronDown className={`h-3 w-3 transition-transform text-white/60 ${
                isOpenDesktop ? "rotate-180" : ""
              }`} />
            </div>
          )}
        </div>
      ) : (
        <PayloadLink
          type={item.type}
          page={item.page}
          url={item.url}
          newTab={item.newTab}
          onClick={() => setActiveDropdown(null)}
          className={cn("px-1 transition-all duration-300 block", collapsed ? "py-2" : "py-4")}
        >
          {navContent}
        </PayloadLink>
      )}

      {/* Desktop dropdown is now handled by MegaMenu component */}
    </div>
  );
}
